import axios, { AxiosInstance, isAxiosError } from "axios";
import {
    CustomerPurchaseProjection,
    DeliveryReport,
    EmailReferenceType,
    Distributor,
    DistributorPurchaseProjection,
    DomainText,
    DomainTexts,
    GroupChart,
    Participant,
    ParticipantActivity,
    ParticipantActivityType,
    PendingParticipant,
    Product,
    ProductActivation,
    Profile,
    Project,
    PurchasedProduct,
    ResendableEmail,
    Snapshot,
    TextSearchHit,
    Trainer,
    Transaction,
} from "../types/PersologOnlineTypes.ts";
import { Dependency } from "../types/TextMaintenanceTypes.ts";
import { Banner, BannerImage, BannerImageVariant, StartPageBanner } from "../types/BannerTypes.ts";
import { GeneratedProfiles } from "../types/StatisticsTypes.ts";
import {
    CockpitOverview,
    ReleaseNotesOverview,
    CustomerNotesSyncResult,
    ReviewSyncResult,
    SourceData,
    VersionChangelog,
} from "../types/CockpitTypes.ts";
import { keycloak } from "../main.ts";

// Identifies this app as the origin of a request, so a failing backend request can be
// traced back here in the logs (sent as the X-Client header on every call).
const CLIENT_APP = "admin";
const newRequestId = (): string =>
    globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(16).slice(2)}`;

const backendBaseUrl = import.meta.env.VITE_APP_BACKEND_API_BASE_URL;
const languageManagementPath = import.meta.env.VITE_APP_LANGUAGE_MANAGEMENT_PATH;
const profileAdministrationPath = import.meta.env.VITE_APP_PROFILE_ADMINISTRATION_PATH;
const profileContractorPath = import.meta.env.VITE_APP_PROFILE_CONTRACTOR_PATH;
const cockpitPath = import.meta.env.VITE_APP_COCKPIT_PATH;
const emailServicePath = import.meta.env.VITE_APP_EMAIL_SERVICE_PATH;

// No request should be able to hang the UI indefinitely.
const REQUEST_TIMEOUT_MS = 30000;

const languageManagementAxiosInstance = axios.create({
    baseURL: `${backendBaseUrl}${languageManagementPath}`,
    timeout: REQUEST_TIMEOUT_MS,
});

const profileAdminAxiosInstance = axios.create({
    baseURL: `${backendBaseUrl}${profileAdministrationPath}`,
    timeout: REQUEST_TIMEOUT_MS,
});

const profileContractorAxiosInstance = axios.create({
    baseURL: `${backendBaseUrl}${profileContractorPath}`,
    timeout: REQUEST_TIMEOUT_MS,
});

// Dev Cockpit aggregator (persolog-online/cockpit): open MRs, Jira RDT/POS, versions, profile-gen health.
const cockpitAxiosInstance = axios.create({
    baseURL: `${backendBaseUrl}${cockpitPath}`,
    timeout: REQUEST_TIMEOUT_MS,
});

// e-mail-service: read-only delivery status of the emails we handed to Scaleway (RDT-360).
const emailServiceAxiosInstance = axios.create({
    baseURL: `${backendBaseUrl}${emailServicePath}`,
    timeout: REQUEST_TIMEOUT_MS,
});

function setupInterceptor(apiInstance: AxiosInstance) {
    apiInstance.interceptors.request.use(
        async (config) => {
            try {
                // Access tokens are short-lived (5 min): refresh before every request so
                // long editing sessions never send an expired token (which made saves
                // fail with 401 and lose the user's pending changes).
                await keycloak.updateToken(30);

                if (keycloak.token) {
                    config.headers.Authorization = `Bearer ${keycloak.token}`;
                }
            } catch (err) {
                console.error("Token refresh failed", err);
                const tokenError = new Error("Token expired and refresh failed");
                (tokenError as Error & { cause?: unknown }).cause = err;
                throw tokenError;
            }

            config.headers["X-Request-Id"] = newRequestId();
            config.headers["X-Client"] = CLIENT_APP;

            return config;
        },
        (error: Error) => {
            return Promise.reject(error);
        },
    );
}

setupInterceptor(languageManagementAxiosInstance);
setupInterceptor(profileAdminAxiosInstance);
setupInterceptor(profileContractorAxiosInstance);
setupInterceptor(cockpitAxiosInstance);
setupInterceptor(emailServiceAxiosInstance);

export const EmailServiceAPI = {
    /**
     * What became of the emails sent to this address (RDT-360). Scaleway is asked live, so this is
     * the same information support used to read in the Scaleway console.
     *
     * Rejects with 503 when the environment has no Scaleway key configured and 502 when Scaleway
     * could not be reached — the caller has to tell those apart from "nothing was ever sent".
     */
    async getDeliveryStatus(
        recipient: string,
        options: { days?: number; referenceType?: EmailReferenceType; referenceId?: string } = {},
    ): Promise<DeliveryReport> {
        const { data } = await emailServiceAxiosInstance.get("/email/delivery", {
            params: {
                recipient,
                ...(options.days === undefined ? {} : { days: options.days }),
                // Hyphenated on purpose: that is what the endpoint takes.
                ...(options.referenceType && options.referenceId
                    ? { "reference-type": options.referenceType, "reference-id": options.referenceId }
                    : {}),
            },
        });
        return data;
    },
};

export const CockpitAPI = {
    async getOverview(): Promise<CockpitOverview> {
        const { data } = await cockpitAxiosInstance.get("/overview");
        return data;
    },

    // Forces the cockpit to re-poll every source now (not just read its cache), then returns the fresh
    // snapshot. Backs the manual sync button — takes a few seconds since it hits GitLab/Jira live.
    async forceRefresh(): Promise<CockpitOverview> {
        const { data } = await cockpitAxiosInstance.post("/refresh");
        return data;
    },

    // Deletes via the cockpit's backend-role service account (a plain admin user token can't delete
    // a participant it doesn't own — that takes the backend or data-protection role, RDT-474).
    async deleteParticipant(participantId: number): Promise<void> {
        await cockpitAxiosInstance.delete(`/participants/${participantId}`);
    },

    // The Release Notes deployment timeline (RDT-211): when each service version reached each env.
    // An instant read from the cockpit's in-memory cache.
    async getReleaseNotes(): Promise<SourceData<ReleaseNotesOverview>> {
        const { data } = await cockpitAxiosInstance.get("/release-notes");
        return data;
    },

    // The per-version changelog (RDT-211): the RDT issues + raw commits that shipped in a service
    // version. Built on demand by the cockpit when a timeline row is expanded.
    async getChangelog(service: string, version: string): Promise<VersionChangelog> {
        const { data } = await cockpitAxiosInstance.get("/release-notes/changelog", {
            params: { service, version },
        });
        return data;
    },

    // Sync Done + review-relevant issues onto the current/next Confluence review page (RDT-287).
    // dryRun=true previews (target page + issues) without writing.
    async syncReviewPage(dryRun: boolean): Promise<ReviewSyncResult> {
        const { data } = await cockpitAxiosInstance.post("/review/sync", null, { params: { dryRun } });
        return data;
    },

    // Publish the customer-relevant release notes to profile-administration, which serves the public
    // release-notes page (RDT-286). dryRun=true returns exactly what would be published.
    async syncCustomerNotes(dryRun: boolean): Promise<CustomerNotesSyncResult> {
        const { data } = await cockpitAxiosInstance.post("/release-notes/customer-sync", null, {
            params: { dryRun },
        });
        return data;
    },
};

export const LanguageManagementAPI = {
    async getProducts(active: boolean): Promise<ProductActivation[]> {
        const { data } = await languageManagementAxiosInstance.get("/activation/products?active=" + active);
        return data.products;
    },

    async activateOrDeactivateProduct(product: string, language: string, active: boolean): Promise<ProductActivation> {
        const { data } = await languageManagementAxiosInstance.put(`/activation/products/${product}?language=${language}&active=${active}`);
        return data;
    },

    async getLanguages(): Promise<string[]> {
        const { data } = await languageManagementAxiosInstance.get("/activation/languages?");
        return data;
    },

    async getAllNamespaces(stage: string): Promise<ProductActivation[]> {
        const { data } = await languageManagementAxiosInstance.get(`/orchestrator/${stage}/namespaces`);
        return data;
    },

    async releaseTemplate(product: string): Promise<void> {
        await languageManagementAxiosInstance.post(`/template/${product}`);
    },

    async getTextsForProductAndLanguage(stage: string, productId: string, language: string): Promise<DomainTexts> {
        const { data } = await languageManagementAxiosInstance.get(`orchestrator/${stage}/${productId}/${language}`);
        return data;
    },

    // Full-text search across all namespaces of a stage in one request: matches the query
    // (case-insensitive, server-side) against both identifier and text. Optional `languages`
    // narrows the search; empty means all languages.
    async searchTexts(stage: string, query: string, languages: string[]): Promise<TextSearchHit[]> {
        const { data } = await languageManagementAxiosInstance.get(`/orchestrator/${stage}/search`, {
            params: {
                q: query,
                ...(languages.length ? { languages: languages.join(",") } : {}),
            },
        });
        return data;
    },

    // Bulk fetch: every namespace (profile-/questionnaire-/default) × every language for a
    // base product in one round-trip. Falls back at the call site if the endpoint is absent.
    async getAllTextsForProduct(
        stage: string,
        productId: string
    ): Promise<Array<{ product: string; language: string; domainTexts: DomainText[] }>> {
        const { data } = await languageManagementAxiosInstance.get(`orchestrator/${stage}/${productId}`);
        return data;
    },

    async saveAllTextsForProduct(productId: string, language: string, texts: DomainTexts): Promise<void> {
        await languageManagementAxiosInstance.put(`/orchestrator/${productId}/${language}`, texts);
    },

    // Bulk save: every changed (namespace, language) in one request + one backend transaction.
    // Falls back at the call site to per-language saves if the endpoint is absent.
    async saveAllTextsForProductBulk(
        baseProductId: string,
        entries: Array<{ product: string; language: string; domainTexts: DomainText[] }>
    ): Promise<void> {
        await languageManagementAxiosInstance.put(`/orchestrator/${baseProductId}/bulk`, entries);
    },

    async getLatestSnapshot(stage: string, product: string, language: string): Promise<Snapshot> {
        try {
            const { data } = await languageManagementAxiosInstance.get(
                `/orchestrator/${stage}/${product}/${language}/snapshot/latest`
            );
            return data;
        } catch (error: any) {
            if (error.response?.status === 404) {
                return {
                    product,
                    language,
                    version: 0,
                    createdAt: "",
                    createdBy: "",
                    texts: [],
                };
            }
            throw error;
        }
    },

    async releaseSnapshot(product: string, language: string): Promise<void> {
        await languageManagementAxiosInstance.post(`/orchestrator/${product}/${language}/snapshot/release`);
    },

    async cleanUpProductDomain(product: string, language: string, stage: string = "STAGING"): Promise<void> {
        await languageManagementAxiosInstance.delete(`/orchestrator/${stage}/${product}/${language}/cleanup`);
    },

    async getTextDependencies(stage: string): Promise<Dependency[]> {
        const { data } = await languageManagementAxiosInstance.get(`/orchestrator/${stage}/dependencies`);
        return data;
    },

    // Notification banners (RDT-339). No stage parameter on purpose: banners are maintained per
    // environment, so this admin frontend writes to the language-management of its OWN environment.
    // Staging banners are maintained in the staging admin, production banners in the production one.
    async getBanners(): Promise<Banner[]> {
        const { data } = await languageManagementAxiosInstance.get(`/banners/all`);
        return data.banners;
    },

    async createBanner(banner: Banner): Promise<Banner> {
        const { data } = await languageManagementAxiosInstance.post(`/banners`, banner);
        return data;
    },

    async updateBanner(id: number, banner: Banner): Promise<Banner> {
        const { data } = await languageManagementAxiosInstance.put(`/banners/${id}`, banner);
        return data;
    },

    async deleteBanner(id: number): Promise<void> {
        await languageManagementAxiosInstance.delete(`/banners/${id}`);
    },

    // Start-page galleria banners (RDT-359). Same per-environment story as the notification
    // banners above: no stage parameter, each admin writes to its own language-management.
    async getStartPageBanners(): Promise<StartPageBanner[]> {
        const { data } = await languageManagementAxiosInstance.get(`/start-page-banners/all`);
        return data.banners;
    },

    async createStartPageBanner(banner: StartPageBanner): Promise<StartPageBanner> {
        const { data } = await languageManagementAxiosInstance.post(`/start-page-banners`, banner);
        return data;
    },

    async updateStartPageBanner(id: number, banner: StartPageBanner): Promise<StartPageBanner> {
        const { data } = await languageManagementAxiosInstance.put(`/start-page-banners/${id}`, banner);
        return data;
    },

    async deleteStartPageBanner(id: number): Promise<void> {
        await languageManagementAxiosInstance.delete(`/start-page-banners/${id}`);
    },

    async getBannerImages(): Promise<BannerImage[]> {
        const { data } = await languageManagementAxiosInstance.get(`/banner-images`);
        return data.images;
    },

    /**
     * Uploads one image. The response describes what the api STORED, not what was sent: it scales the
     * upload to the variant's width and re-encodes it as WebP, so a 3 MB photo comes back as ~120 KB.
     * No explicit Content-Type on purpose — the browser has to set the multipart boundary itself.
     */
    async uploadBannerImage(file: File, variant: BannerImageVariant): Promise<BannerImage> {
        const body = new FormData();
        body.append("file", file);
        const { data } = await languageManagementAxiosInstance.post(`/banner-images`, body, {
            params: { variant },
        });
        return data;
    },

    async deleteBannerImage(hash: string): Promise<void> {
        await languageManagementAxiosInstance.delete(`/banner-images/${hash}`);
    }
};

export const ProfileAdministrationAPI = {
    async fetchParticipants(email: string): Promise<Participant[]> {
        if (email) {
            const encodedEmail = btoa(email);
            const { data } = await profileAdminAxiosInstance.get(`/participants/participant/email/${encodedEmail}`);
            return data.participants.map((p: Participant) => ({ ...p, showDetails: false }));
        }
        return [];
    },

    /**
     * The participants with these ids, for support working through a list of ids (RDT-497). An id that
     * does not exist is left out; the caller compares to tell which.
     */
    async fetchParticipantsByIds(ids: number[]): Promise<Participant[]> {
        const participants: Participant[] = [];
        for (const id of ids) {
            try {
                const { data } = await profileAdminAxiosInstance.get(`/participants/participant/${id}`);
                participants.push({ ...data, showDetails: false });
            } catch (error) {
                if (!isAxiosError(error) || error.response?.status !== 404) throw error;
            }
        }
        return participants;
    },

    async fetchPendingParticipants(): Promise<PendingParticipant[]> {
        const { data } = await profileAdminAxiosInstance.get(`/participants/pending`);
        return data;
    },

    // Hard-deletes the participant with all their data on every service (RDT-470 chain). Sent with
    // the admin's own token: the backend only allows it for the data-protection role or the owning
    // trainer (RDT-474), so this must not go through a service-account proxy.
    async deleteParticipant(participantId: number): Promise<void> {
        await profileAdminAxiosInstance.delete(`/participants/${participantId}`);
    },

    async fetchTrainer(id: string): Promise<Trainer> {
        const { data } = await profileAdminAxiosInstance.get(`/trainer/${id}`);
        return data;
    },

    async fetchParticipantsByProject(projectId: number): Promise<Participant[]> {
        if (projectId) {
            const { data } = await profileAdminAxiosInstance.get(`/participants/${projectId}`);
            return data.participants.map((p: Participant) => ({ ...p }));
        }
        return [];
    },

    async fetchTrainers(email: string): Promise<Trainer[]> {
        if (email) {
            const encodedEmail = btoa(email);
            const { data } = await profileAdminAxiosInstance.get(`/trainer/email/${encodedEmail}`);
            return data.trainers.map((t: Trainer) => ({ ...t, showDetails: false }));
        }
        return [];
    },

    async fetchDistributors(): Promise<Distributor[]> {
        const { data } = await profileAdminAxiosInstance.get(`/trainer/distributors`);
        return data.distributors;
    },

    async putTrainer(trainer: Trainer): Promise<Trainer> {
        const { data } = await profileAdminAxiosInstance.put(`/trainer/${trainer.id}`, trainer);
        return data;
    },

    async fetchProjects(trainerId: string): Promise<Project[]> {
        const response = await profileAdminAxiosInstance.get(`/projects/${trainerId}/product/all`);
        return response.data.projects;
    },

    async fetchProject(projectId: number): Promise<Project> {
        const response = await profileAdminAxiosInstance.get(`/projects/${projectId}`);
        return response.data;
    },

    /**
     * Fetches purchased products
     * @param trainer 
     * @returns 
     */
    async fetchProducts(trainer: string): Promise<PurchasedProduct[]> {
        const { data } = await profileAdminAxiosInstance.get(`/products/${trainer}`);
        return data.purchasedProducts;
    },

        /**
     * Fetches all trainer's products from the backend, including distributor-specific and previously purchased ones.
     * Unavailable products are included if previously purchased.
     * @param trainerId
     * @returns list of avaliable products for the trainer
     */
    async fetchAllProductsForTrainer(trainerId: string): Promise<Product[]> {
        const { data } = await profileAdminAxiosInstance.get(`/products?trainer=${trainerId}`);
        return data.products;
    },

    async manageProductQuantityFromAdmin(product: PurchasedProduct, newQuantity: number): Promise<PurchasedProduct> {
        const { data } = await profileAdminAxiosInstance.put(`/products/manage/${newQuantity}?fromAdmin=true`, {
            product: product.product,
            quantity: product.quantity,
            trainer: product.trainer,
        });
        return data;
    },

    async fetchCustomerPurchases(fromDate: string, toDate: string): Promise<CustomerPurchaseProjection[]> {
        let queryParams = "";
        if (fromDate && toDate) {
            queryParams = `?from=${fromDate}&to=${toDate}`;
        }

        const { data } = await profileAdminAxiosInstance.get(`/purchases/billing/customers${queryParams}`);
        return data;
    },

    async fetchDistributorPurchases(fromDate: string, toDate: string): Promise<DistributorPurchaseProjection[]> {
        let queryParams = "";
        if (fromDate && toDate) {
            queryParams = `?from=${fromDate}&to=${toDate}`;
        }

        const { data } = await profileAdminAxiosInstance.get(`/purchases/billing/distributors${queryParams}`);
        return data;
    },

    async getGroupCharts(): Promise<string[]> {
        const { data } = await profileAdminAxiosInstance.get("/group-chart");
        return (data.groupCharts as GroupChart[]).map((groupChart) => groupChart.groupChart);
    },

    async getAllProducts(): Promise<Product[]> {
        const { data } = await profileAdminAxiosInstance.get("/products");
        return data.products;
    },

    async getTransactions(trainerId: string): Promise<Transaction[]> {
        const { data } = await profileAdminAxiosInstance.get(`/transactions/${trainerId}`);
        return data;
    },

    /**
     * The projects a participant may be moved into (RDT-41). The backend decides compatibility
     * (target must not be a direct-link project, product and recipients must match), so the result
     * is the authoritative list of targets — including the participant's current project.
     */
    async fetchCompatibleProjects(trainerId: string, participantId: number): Promise<Project[]> {
        const { data } = await profileAdminAxiosInstance.get(`/projects/${trainerId}/${participantId}/compatible`);
        return data.projects;
    },

    /**
     * Moves a participant into another project. Rejects with 400 if the target is incompatible.
     */
    async moveParticipant(targetProjectId: number, participantId: number): Promise<Participant> {
        const { data } = await profileAdminAxiosInstance.post(`/participants/${targetProjectId}/move/${participantId}`);
        return data;
    },

    /**
     * Discards the participant's questionnaire so it can be filled in again (RDT-39): the backend
     * deletes the stored answers and puts the participant back to ACTIVATED. Rejects with 409 when the
     * participant's status does not allow a reset, and with 502 when the answers could not be deleted.
     */
    async resetQuestionnaire(participantId: number): Promise<Participant> {
        const { data } = await profileAdminAxiosInstance.post(`/participants/${participantId}/reset-questionnaire`);
        return data;
    },

    /**
     * Sends the questionnaire invitation or the profile email to a participant again (RDT-38).
     *
     * Rejects with 409 when the participant's status makes the email pointless and with 502 when the
     * e-mail-service could not be reached — in both cases nothing was sent.
     */
    async resendEmail(participantId: number, type: ResendableEmail): Promise<Participant> {
        const { data } = await profileAdminAxiosInstance.post(`/participants/${participantId}/resend-email`, null, {
            params: { type },
        });
        return data;
    },

    /**
     * The participant's activity history (RDT-319) — the same list the trainer sees in the project.
     * Needs the widened admin authorization on the endpoint; without it the call answers 403.
     */
    async getParticipantActivity(participantId: number): Promise<ParticipantActivity[]> {
        const { data } = await profileAdminAxiosInstance.get(`/activities/participant/${participantId}`);
        return data.participantActivities;
    },

    /**
     * Appends an entry to the participant's activity log. The backend expects a JSON document that
     * maps onto its ParticipantActivityType by name, so the value has to be stringified explicitly:
     * axios passes a raw string body through untouched, which would send an unquoted — and therefore
     * invalid — JSON body.
     */
    async logParticipantActivity(participantId: number, activityType: ParticipantActivityType): Promise<void> {
        await profileAdminAxiosInstance.post(`/activities/participant/${participantId}`, JSON.stringify(activityType), {
            headers: { "Content-Type": "application/json" },
        });
    },
};

export const ProfileContractorApi = {
    /**
     * Profiles first generated in [from, to) per product, real and test apart (RDT-435). `from` and `to`
     * are ISO instants: the boundaries of the picked days in the browser's time zone.
     */
    async fetchGeneratedProfiles(from: string, to: string): Promise<GeneratedProfiles> {
        const { data } = await profileContractorAxiosInstance.get("/statistics/profiles", { params: { from, to } });
        return data;
    },

    async fetchPendingProfiles(): Promise<Profile[]> {
        const { data } = await profileContractorAxiosInstance.get("/generate/pending");
        return data.pendingProfiles;
    },

    async deleteProfile(profile: Profile) {
        await profileContractorAxiosInstance.delete(`/generate/${profile.uuid}`);
    },

    async recalculateProfile(profile: Profile) {
        await profileContractorAxiosInstance.post(`/generate/calculate/${profile.id}`);
    },

    async regenerateProfile(profile: Profile) {
        await profileContractorAxiosInstance.post(`/generate/${profile.product}/${profile.id}/${profile.hash}`, null, {
            params: { language: profile.language },
        });
    },

    /**
     * Renders every existing PDF of the participant again without notifying anyone and without changing
     * the participant (RDT-497). Resolves with the languages requested; 404 when the participant has no
     * PDF, 409 when they are deleted or revoked.
     */
    async regenerateProfileSilently(participantId: number): Promise<{ id: number; languages: string[] }> {
        const { data } = await profileContractorAxiosInstance.post(`/generate/${participantId}/regenerate-silently`);
        return data;
    },

    async regenerateGroupChart(profile: Profile) {
        // profile.id is the groupChartId (pending PDFs are keyed by it), so target the
        // chart-level regenerate endpoint rather than the project-level one.
        await profileContractorAxiosInstance.post(`/generate/${profile.product}/${profile.id}/group/regenerate`, null, {
            params: { language: profile.language },
        });
    },
};
